// Phase 21 · Audio room view (Discord Stage style)
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Radio, PlayCircle, LogIn, LogOut, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import useEventPolling from "../../../hooks/useEventPolling";
import SpeakerStageCard from "../../../components/community_v4/SpeakerStageCard";
import ParticipantTile from "../../../components/community_v4/ParticipantTile";
import ReactionsBar from "../../../components/community_v4/ReactionsBar";
import RoomChatPanel from "../../../components/community_v4/RoomChatPanel";
import HostControlsPanel from "../../../components/community_v4/HostControlsPanel";
import SpeakerRequestButton from "../../../components/community_v4/SpeakerRequestButton";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AudioRoomView() {
    const { roomId } = useParams();
    const { user } = useAuth();
    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setDisabled(false);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); return; }
            if (r.status === 404) { setRoom(null); return; }
            if (r.ok) setRoom(await r.json());
        } catch {}
        finally { setLoading(false); }
    }, [roomId]);

    useEffect(() => { load(); }, [load]);

    // Poll participants every 30s
    const { data: partsData, refetch: refetchParts } = useEventPolling(
        room ? `${API}/api/community/rooms/${room.id}/participants` : null,
    );
    // Poll speaker requests (host view) every 30s
    const { data: reqsData, refetch: refetchReqs } = useEventPolling(
        room?.is_host ? `${API}/api/community/rooms/${room.id}/speaker-requests?status=pending` : null,
    );

    const parts = partsData?.items || [];
    const speakers = parts.filter((p) => p.role === "host" || p.role === "speaker");
    const listeners = parts.filter((p) => p.role === "listener");
    const reqs = reqsData?.items || [];

    const joinRoom = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/join`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("Joined"); load(); refetchParts(); }
            else if (r.status === 422) {
                const d = await r.json();
                toast.error(d?.detail?.error === "room_full" ? "Room full" : "Cannot join");
            }
            else toast.error("Join failed");
        } catch {}
        setBusy(false);
    };

    const leaveRoom = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/leave`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("Left"); load(); refetchParts(); }
        } catch {}
        setBusy(false);
    };

    const goLive = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/go-live`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("You're live!"); load(); }
            else toast.error("Go-live failed");
        } catch {}
        setBusy(false);
    };

    const demote = async (p) => {
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/participants/${p.user_id}/demote`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("Demoted"); refetchParts(); }
        } catch {}
    };
    const toggleMute = async (p) => {
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/participants/${p.user_id}/toggle-mute`, { method: "POST", credentials: "include" });
            if (r.ok) { refetchParts(); }
        } catch {}
    };

    if (loading) return <div className="min-h-screen bg-[#0A0A0A] text-neutral-500 flex items-center justify-center">Loading…</div>;
    if (disabled) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6 text-center">
            <div><p className="text-[#FAFAFA] mb-2">Rooms engine disabled</p><Link to="/community" className="text-neutral-400 underline">← Community</Link></div>
        </div>
    );
    if (!room) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6 text-center">
            <div><p className="text-neutral-500 mb-2">Room not found</p><Link to="/rooms" className="text-[#FFFFFF]">← Rooms</Link></div>
        </div>
    );

    const isParticipant = room.viewer_role != null;

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="audio-room-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/rooms" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Rooms
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <div className="flex items-center gap-2 min-w-0">
                        <h1 className="text-base font-semibold truncate" data-testid="room-title-header">{room.title}</h1>
                        {room.status === "live" && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FAFAFA] text-black font-bold animate-pulse">
                                <Radio size={9} /> LIVE
                            </span>
                        )}
                        {room.status === "scheduled" && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-neutral-700/40 text-neutral-300 font-bold">SCHEDULED</span>
                        )}
                        {room.status === "ended" && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-neutral-700/40 text-neutral-400 font-bold">ENDED</span>
                        )}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {room.is_host && room.status === "scheduled" && (
                            <button
                                type="button"
                                onClick={goLive}
                                disabled={busy}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-white font-semibold rounded-lg text-xs"
                                data-testid="room-go-live-btn"
                            >
                                <PlayCircle size={13} /> Go live
                            </button>
                        )}
                        {!room.is_host && room.status === "live" && !isParticipant && user && (
                            <button
                                type="button"
                                onClick={joinRoom}
                                disabled={busy}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-neutral-900 font-semibold rounded-lg text-xs"
                                data-testid="room-join-btn"
                            >
                                <LogIn size={13} /> Join
                            </button>
                        )}
                        {!room.is_host && isParticipant && (
                            <button
                                type="button"
                                onClick={leaveRoom}
                                disabled={busy}
                                className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#1A1A1A] hover:border-[#FAFAFA]/40 hover:text-[#FAFAFA] text-neutral-400 rounded-lg text-xs"
                                data-testid="room-leave-btn"
                            >
                                <LogOut size={13} /> Leave
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                <section className="space-y-4">
                    {/* Stage */}
                    <div className="bg-gradient-to-b from-[#111111] to-[#111111] border border-[#1A1A1A] rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">
                                Stage · {speakers.length}/{room.max_speakers} speakers
                            </p>
                            <SpeakerRequestButton room={room} onRequested={refetchReqs} />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {speakers.map((p) => (
                                <SpeakerStageCard
                                    key={p.id}
                                    p={p}
                                    isHostView={room.is_host}
                                    onDemote={demote}
                                    onToggleMute={toggleMute}
                                />
                            ))}
                            {!speakers.length && (
                                <p className="col-span-full text-center text-sm text-neutral-500 italic">
                                    No speakers on stage
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Listeners */}
                    <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-4">
                        <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-1 mb-3">
                            <Users size={11} /> Audience · {listeners.length}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {listeners.map((p) => (
                                <div
                                    key={p.id}
                                    className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#0A0A0A] border border-[#1A1A1A]"
                                    data-testid={`listener-${p.user_id}`}
                                >
                                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-neutral-900 text-[10px] font-bold">
                                        {p.username?.[0]?.toUpperCase() || "?"}
                                    </div>
                                    <span className="text-[11px] text-neutral-300">@{p.username}</span>
                                </div>
                            ))}
                            {!listeners.length && (
                                <p className="text-xs text-neutral-500 italic">No listeners yet</p>
                            )}
                        </div>
                    </div>

                    {/* Reactions bar */}
                    {isParticipant && room.status === "live" && (
                        <ReactionsBar roomId={room.id} canReact={true} />
                    )}
                </section>

                <aside className="space-y-4 lg:sticky lg:top-[68px] lg:self-start h-[720px]">
                    {room.is_host && <HostControlsPanel room={room} requests={reqs} onRefresh={() => { load(); refetchParts(); refetchReqs(); }} />}
                    <RoomChatPanel room={room} viewer={user} canSend={isParticipant} />
                </aside>
            </main>
        </div>
    );
}
